import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import PropTypes from 'prop-types';
import React, {Fragment, useEffect, useState} from 'react';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import copy from 'copy-to-clipboard';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';

import SceneListItem from '@explorer/components/items-panel/scene-item';
import {
  IndicatorsContainer,
  InstrumentsIndicator,
  PartialRectIndicator,
} from '@explorer/components/ui/metadata-indicators';
import {
  ItemsCount,
  GroupTitle as Title,
} from '@explorer/components/items-panel/item-components';
import {LABS_MAP, UI_MODES} from '@explorer/constants';
import {PS_ITEM_TYPES} from '@explorer/constants/item-types';
import {
  addItemsToOrder,
  addItemsToView,
  removeItemsFromOrder,
  removeItemsFromView,
} from '@explorer/actions/results';
import {
  focusItem,
  focusItems,
  unfocusAllItems,
} from '@explorer/actions/focused-item';
import {formatInstrumentLabel} from '@explorer/util/filters';
import {getSortedGroupItems} from '@explorer/selectors/results';
import {getUniqueId} from '@explorer/util/items';
import {
  hasPartialRectification,
  isCompareSubject,
} from '@explorer/util/results';
import {notify} from '@explorer/actions/notifications';
import {trackEvent} from '@explorer/analytics';

const ScenesList = props => {
  const {t} = useTranslation();

  // all collapsed by default
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (props.activeResult?.id) {
      trackEvent('Scene List: Shown', {
        itemGroupsCount: props.itemGroups.length,
      });
    }
  }, [props.activeResult, props.itemGroups]);

  if (!props.activeResult) {
    return false;
  }

  const onSelectAllClick = (fullySelected, features, groupNumber) => event => {
    event.stopPropagation();

    const featureIds = features.map(getUniqueId);

    const viewPayload = {
      items: featureIds,
      resultId: props.activeResult.id,
    };

    fullySelected
      ? props.removeItemsFromView(viewPayload)
      : props.addItemsToView(viewPayload);

    const orderItemsIds = features
      .filter(feature => feature.properties.available)
      .map(getUniqueId);

    // don't bother if things can't be ordered
    if (orderItemsIds.length) {
      const orderPayload = {
        items: orderItemsIds,
        resultId: props.activeResult.id,
      };
      fullySelected
        ? props.removeItemsFromOrder(orderPayload)
        : props.addItemsToOrder(orderPayload);
    }

    const itemType = props.activeResult.id.split(':')[0];

    trackEvent(
      fullySelected
        ? 'Scene List: Deselect all in strip'
        : 'Scene List: Select all in strip',
      {
        itemsCount: featureIds.length,
        itemType,
        groupNumber: groupNumber + 1,
        groupCount: props.itemGroups.length,
      }
    );
  };

  const onItemClick = item => () => {
    const uniqueId = getUniqueId(item);

    const payload = {
      items: [uniqueId],
      resultId: props.activeResult.id,
    };

    props.viewItems.includes(uniqueId)
      ? props.removeItemsFromView(payload)
      : props.addItemsToView(payload);

    // has download perms add to order
    if (item.properties.available) {
      props.orderItems.includes(uniqueId)
        ? props.removeItemsFromOrder(payload)
        : props.addItemsToOrder(payload);
    }
  };

  const handleShareLink = item => () => {
    const baseURL = window.location.href.split('#')[0];
    const shareUrl = `${baseURL}?item=${item.properties.item_type}::${item.id}`;

    copy(shareUrl);

    props.notify({
      message: t('resultsList.notifyCopied'),
    });
  };

  const handleClick = (id, groupNumber) => {
    setExpanded({...expanded, [id]: !expanded[id]});

    const itemType = props.activeResult.id.split(':')[0];

    trackEvent(
      !expanded[id] ? 'Scene List: Expand strip' : 'Scene List: Collapse strip',
      {
        itemType,
        groupNumber: groupNumber + 1,
        groupCount: props.itemGroups.length,
      }
    );
  };

  return (
    <Fragment>
      {props.itemGroups.map((group, index) => {
        const featureIds = group.features.map(getUniqueId);
        const countSelected = featureIds.filter(id =>
          props.viewItems.includes(id)
        ).length;
        const fullySelected = countSelected === featureIds.length;
        const anySelected = countSelected > 0;

        const firstGroupItem = group.features[0];

        // Calculcate the group (and all group items) capabilities label

        return (
          <Fragment key={group.id}>
            <ListItemButton
              divider
              onClick={() => handleClick(group.id, index)}
              onMouseEnter={() =>
                props.focusItems({items: group.features, reset: true})
              }
              onMouseLeave={() => props.unfocusAllItems()}
              sx={{
                bgcolor: 'grey.100',
                paddingX: 1,
              }}
              data-qe={`scenes-grouphead-${firstGroupItem.properties.satellite_id}`}
            >
              <ListItemIcon sx={{minWidth: 0, marginRight: 1}}>
                <Tooltip
                  title={`${
                    fullySelected
                      ? t('scenesList.deselectAll', 'Deselect all in group')
                      : t('scenesList.selectAll', 'Select all in group')
                  }`}
                >
                  <Checkbox
                    checked={anySelected}
                    indeterminate={!fullySelected && anySelected}
                    onClick={onSelectAllClick(
                      fullySelected,
                      group.features,
                      index
                    )}
                    data-qe="scenes-checkbox"
                  />
                </Tooltip>
              </ListItemIcon>
              <ListItemText disableTypography>
                <ItemsCount
                  selected={countSelected}
                  total={group.features.length}
                  data-qe="scenes-item-count"
                />
                <Box>
                  <Title data-qe="scenes-datetime">
                    {t('common:date_time.short', {
                      date: firstGroupItem.properties.acquired,
                    })}
                  </Title>
                  {firstGroupItem.properties.satellite_id && (
                    <Tooltip title={t('resultsList.tooltipSatelliteId')}>
                      <Typography
                        variant="body2"
                        display="inline"
                        data-qe="satellite-id"
                      >
                        {' '}
                        – {firstGroupItem.properties.satellite_id}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
                <IndicatorsContainer>
                  {
                    // Show Instruments Indicator for PlanetScope only
                    PS_ITEM_TYPES.includes(
                      firstGroupItem.properties.item_type
                    ) && (
                      <InstrumentsIndicator
                        label={formatInstrumentLabel(
                          firstGroupItem.properties.instrument
                        )}
                      />
                    )
                  }
                  {hasPartialRectification(group.features) && (
                    <PartialRectIndicator data-qe="scenes-partial-rectification-indicator" />
                  )}
                </IndicatorsContainer>
              </ListItemText>
              {expanded[group.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
            <Collapse in={!!expanded[group.id]} timeout="auto" unmountOnExit>
              <List disablePadding>
                {group.features.map(feature => {
                  const uniqueId = getUniqueId(feature);
                  const available = feature.properties.available;
                  const selected = props.isComparing
                    ? isCompareSubject(props.subjects, feature.id) ||
                      isCompareSubject(props.subjects, props.activeResult.id)
                    : props.viewItems.includes(uniqueId);
                  return (
                    <SceneListItem
                      key={feature.id}
                      item={feature}
                      selected={selected}
                      available={available}
                      onClick={onItemClick(feature)}
                      onFocus={props.focusItem}
                      onRequestAccess={props.onRequestAccess}
                      onShareClick={handleShareLink(feature)}
                      showSharingMenu={props.showSharingMenu}
                      isComparing={props.isComparing}
                      resultId={props.activeResult.id}
                      satId={firstGroupItem.properties.satellite_id}
                    />
                  );
                })}
              </List>
            </Collapse>
          </Fragment>
        );
      })}
    </Fragment>
  );
};

ScenesList.propTypes = {
  activeResult: PropTypes.object,
  addItemsToOrder: PropTypes.func,
  addItemsToView: PropTypes.func,
  focusItem: PropTypes.func,
  focusItems: PropTypes.func,
  isComparing: PropTypes.bool,
  itemGroups: PropTypes.array.isRequired,
  notify: PropTypes.func,
  onRequestAccess: PropTypes.func,
  orderItems: PropTypes.array,
  removeItemsFromOrder: PropTypes.func,
  removeItemsFromView: PropTypes.func,
  showSharingMenu: PropTypes.bool,
  subjects: PropTypes.object,
  viewItems: PropTypes.array,
  unfocusAllItems: PropTypes.func,
};

const mapStateToProps = state => ({
  isComparing: state.ui.mode === UI_MODES.COMPARE,
  itemGroups: getSortedGroupItems(state),
  orderItems: state.results.orderItems,
  showSharingMenu: state.labs[LABS_MAP.RESULT_SCENE_SHARE_MENU],
  subjects: state.compare.subjects,
  viewItems: state.results.viewItems,
});

const mapDispatchProps = {
  addItemsToOrder,
  addItemsToView,
  focusItem,
  focusItems,
  notify,
  removeItemsFromOrder,
  removeItemsFromView,
  unfocusAllItems,
};

export default connect(mapStateToProps, mapDispatchProps)(ScenesList);
